import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import { useMediaQuery } from '../../hooks/use-media-query';
import { ChatCircle, Envelope, Lightning, Clock, User, AndroidLogo, CaretLeft, Chat, WarningCircle, Copy, ArrowLeft, Question } from '@phosphor-icons/react';
import { Phone } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import MessageComposer from './MessageComposer';
import TaskSection from './TaskSection';
import { chatApi } from '../../services/chatApi';
import { motion } from 'framer-motion';

const ChatConversationDetailsPage = ({ conversationId: propConversationId, hideBackButton = false }) => {
  const navigate = useNavigate();
  const { id: paramConversationId } = useParams();
  const conversationId = propConversationId || paramConversationId;
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mobileHeaderRef = React.useRef(null);

  useEffect(() => {
    if (conversationId) {
      loadConversationDetails();
    } else {
      console.error('No conversation ID provided');
      setError('No conversation ID provided');
      setLoading(false);
    }
  }, [conversationId]);

  // Set up real-time message subscription
  useEffect(() => {
    if (!conversationId || !conversation) return;

    const subscription = chatApi.subscribeToMessages(
      conversationId, 
      (payload) => {
        if (payload.new) {
          setMessages(prev => [...prev, payload.new]);
        }
      },
      businessType
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [conversationId, conversation, businessType]);

  const loadConversationDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw accountIdError;

      // Try to fetch from the new chat_conversations table with contact join
      const conversationResult = await supabase
        .from('chat_conversations')
        .select(`
          *,
          contact:contacts(id, name, phone_number, email)
        `)
        .eq('id', conversationId)
        .eq('account_id', accountId)
        .maybeSingle();

      if (conversationResult.error) {
        console.error('Error fetching conversation:', conversationResult.error);

        // If the new table doesn't exist or has issues, try the old structure
        const oldConversationResult = await supabase
          .from('conversation_threads')
          .select(`
            id, created_at, updated_at, contact_id, location_id,
            contact:contacts(id, name, phone_number)
          `)
          .eq('id', conversationId)
          .maybeSingle();

        if (oldConversationResult.error) {
          throw new Error(`Failed to load conversation from both new and old tables: ${conversationResult.error.message}`);
        }

        // Convert old format to new format for compatibility
        const oldConv = oldConversationResult.data;
        const convertedConversation = {
          id: oldConv.id,
          created_at: oldConv.created_at,
          last_activity: oldConv.updated_at,
          customer_identifier: oldConv.contact?.phone_number || oldConv.contact?.name || 'Unknown',
          channel: 'sms', // Assume SMS for old conversations
          status: 'active',
          message_count: 0,
          metadata: {},
          contact: oldConv.contact
        };

        setConversation(convertedConversation);

        // Fetch messages from old texts table
        const oldMessagesResult = await supabase
          .from('texts')
          .select('*')
          .eq('thread_id', conversationId)
          .order('created_at', { ascending: true });

        if (oldMessagesResult.data) {
          const convertedMessages = oldMessagesResult.data.map(msg => ({
            id: msg.id,
            conversation_id: conversationId,
            sender_type: 'customer', // Assume customer for old messages
            message_content: msg.content,
            created_at: msg.created_at,
            channel: 'sms',
            metadata: {}
          }));
          setMessages(convertedMessages);
        }

        setLoading(false);
        return;
      }

      // If new table query succeeded
      const conversation = conversationResult.data;
      setConversation(conversation);

      // Fetch messages from new chat_messages table
      const messagesResult = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (messagesResult.error) {
        console.error('Error fetching messages:', messagesResult.error);
        // Don't fail completely if messages can't be loaded
        setMessages([]);
      } else {
        setMessages(messagesResult.data || []);
      }

    } catch (error) {
      console.error('Error loading conversation:', error);
      setError(error.message || 'Failed to load conversation details.');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageSent = (result) => {

    // Add optimistic message update
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId,
      sender_type: 'business',
      message_content: result.messageContent || 'Message sent',
      created_at: new Date().toISOString(),
      channel: conversation?.channel || 'sms',
      metadata: {}
    };

    setMessages(prev => [...prev, optimisticMessage]);

    // Refresh conversation after a brief delay to get the real message
    setTimeout(() => {
      loadConversationDetails();
    }, 1000);
  };

  // Helper functions similar to CallDetailsPage
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
      const number = cleaned.length === 11 ? cleaned.slice(1) : cleaned;
      return `(${number.slice(0,3)}) ${number.slice(3,6)}-${number.slice(6)}`;
    }
    return phoneNumber;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const shouldShowContactName = (conversation) => {
    return conversation?.contact?.name && 
           !conversation.contact.name.toLowerCase().includes('customer');
  };

  const getDisplayName = (conversation) => {
    if (shouldShowContactName(conversation)) {
      return conversation.contact.name;
    }

    // For SMS/phone, show formatted phone number
    if (conversation?.channel === 'sms' || conversation?.customer_identifier?.match(/^\+?[\d\s\-\(\)]+$/)) {
      return formatPhoneNumber(conversation.customer_identifier);
    }

    // For email, show email address
    if (conversation?.channel === 'email' || conversation?.customer_identifier?.includes('@')) {
      return conversation.customer_identifier;
    }

    // Fallback
    return conversation?.customer_identifier || 'Unknown Contact';
  };

  const getContactInfo = (conversation) => {
    if (conversation?.channel === 'sms') {
      return conversation?.contact?.phone_number || conversation?.customer_identifier;
    } else if (conversation?.channel === 'email') {
      return conversation?.contact?.email || conversation?.customer_identifier;
    }
    return conversation?.customer_identifier;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getCleanContactInfo = (conversation) => {
    const contactInfo = getContactInfo(conversation);
    if (conversation?.channel === 'sms') {
      return contactInfo?.replace(/\D/g, ''); // Clean phone number
    }
    return contactInfo; // Email or other
  };

  const formatMessageTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true,
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'sms':
        return <ChatCircle className="w-5 h-5 text-blue-500" />;
      case 'email':
        return <Envelope className="w-5 h-5 text-green-500" />;
      case 'webchat':
        return <Chat className="w-5 h-5 text-purple-500" />;
      default:
        return <ChatCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  const getSenderIcon = (senderType) => {
    switch (senderType) {
      case 'customer':
        return <User className="w-3 h-3" />;
      case 'business':
        return <User className="w-3 h-3" />;
      case 'assistant':
        return <AndroidLogo className="w-3 h-3" />;
      default:
        return <User className="w-3 h-3" />;
    }
  };

  const getSenderLabel = (senderType) => {
    switch (senderType) {
      case 'customer':
        return 'Customer';
      case 'business':
        return 'Support Team';
      case 'assistant':
        return 'Bella';
      default:
        return 'Unknown';
    }
  };

  const getMessageStyle = (senderType) => {
    switch (senderType) {
      case 'customer':
        return 'bg-gray-100 text-gray-900';
      case 'business':
        return 'bg-blue-500 text-white';
      case 'assistant':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-gray-100 text-gray-900';
    }
  };

  // Dynamic header height calculation
  useEffect(() => {
    const updateMobileHeaderHeight = () => {
      if (mobileHeaderRef.current && window.innerWidth < 768) {
        const headerHeight = mobileHeaderRef.current.offsetHeight;
        document.documentElement.style.setProperty('--mobile-header-height', `${headerHeight}px`);
      }
    };

    // Initial calculation
    updateMobileHeaderHeight();

    // Update on window resize
    const handleResize = () => {
      updateMobileHeaderHeight();
    };

    window.addEventListener('resize', handleResize);

    // Also update when content changes
    const resizeObserver = new ResizeObserver(updateMobileHeaderHeight);
    if (mobileHeaderRef.current) {
      resizeObserver.observe(mobileHeaderRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (mobileHeaderRef.current) {
        resizeObserver.unobserve(mobileHeaderRef.current);
      }
    };
  }, [conversation]); // Re-run when conversation data changes

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading conversation...</p>
          <p className="text-xs text-gray-400 mt-2">ID: {conversationId}</p>
        </div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <WarningCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Conversation</h3>
          <p className="text-gray-500 mb-2">{error || 'Conversation not found'}</p>
          <p className="text-xs text-gray-400 mb-4">Conversation ID: {conversationId}</p>
          {!hideBackButton && (
            <button
              onClick={() => navigate('/monitor')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Activity
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={hideBackButton ? "h-full flex flex-col" : "h-screen bg-gray-50 flex flex-col"}
      style={{
        margin: 0,
        padding: 0,
        overflow: 'hidden'
      }}
    >
      {/* Desktop-only Header */}
      {!hideBackButton && (
        <div className="hidden md:block border-b border-gray-100">
          <div className="container mx-auto py-5 sm:py-6 px-4 sm:px-6 max-w-7xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <motion.button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors"
                  whileHover={{ x: -3 }}
                  transition={{ duration: 0.3 }}
                >
                  <ArrowLeft className="w-5 h-5" />
                </motion.button>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Chat Details</h1>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile-only Header - Fixed at top of viewport */}
      {conversation && (
        <div 
          ref={mobileHeaderRef}
          className="md:hidden fixed top-0 left-0 right-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-sm"
          style={{
            paddingTop: 0
          }}
        >
          <div className="p-4 sm:p-5">
            {/* First Row: Back Arrow + Name/Title + Copy Button + Date */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {!hideBackButton && (
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-6 h-6 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                  >
                    <CaretLeft className="w-5 h-5" />
                  </button>
                )}
                <span className="text-lg font-medium text-gray-900 truncate">
                  {getDisplayName(conversation)}
                </span>
                {!shouldShowContactName(conversation) && (
                  <button
                    onClick={() => copyToClipboard(getCleanContactInfo(conversation))}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    title={`Copy ${conversation.channel === 'email' ? 'email' : 'phone number'}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="text-right text-xs text-gray-500 flex-shrink-0">
                <div className="font-medium">{formatDate(conversation?.created_at)}</div>
              </div>
            </div>

            {/* Second Row: Channel Icon + Contact Info (only if showing contact name) */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                {getChannelIcon(conversation.channel)}
              </div>
              {shouldShowContactName(conversation) && (
                <>
                  <span className="text-sm text-gray-500 font-normal">
                    {conversation.channel === 'sms' 
                      ? formatPhoneNumber(getContactInfo(conversation))
                      : getContactInfo(conversation)
                    }
                  </span>
                  <button
                    onClick={() => copyToClipboard(getCleanContactInfo(conversation))}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    title={`Copy ${conversation.channel === 'email' ? 'email' : 'phone number'}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={hideBackButton ? "h-full flex flex-col" : "flex-1 flex flex-col px-0 md:px-4 lg:px-6"}>
        <div className={`flex-1 flex flex-col min-h-0 ${conversation ? 'md:mt-0' : ''}`}>
          <Card className={`overflow-hidden shadow-none md:shadow-xl border-0 md:border md:border-gray-100 rounded-none md:rounded-2xl bg-white h-full flex flex-col`}>
            <CardContent className="p-0 h-full flex flex-col">
              {/* Desktop Header */}
              <div className="hidden md:block p-5 sm:p-6 border-b border-gray-200 bg-gray-50/50 flex-shrink-0">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-right text-xs text-gray-500">
                    <div className="font-medium">{formatDate(conversation?.created_at)}</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                      {getChannelIcon(conversation.channel)}
                    </div>
                    <span className="text-lg font-medium text-gray-900">
                      {getDisplayName(conversation)}
                    </span>
                  </div>
                  {shouldShowContactName(conversation) && (
                    <div className="flex items-center gap-2 ml-10 sm:ml-0">
                      <span className="text-sm text-gray-500 font-normal">
                        {conversation.channel === 'sms' 
                          ? formatPhoneNumber(getContactInfo(conversation))
                          : getContactInfo(conversation)
                        }
                      </span>
                      <button
                        onClick={() => copyToClipboard(getCleanContactInfo(conversation))}
                        className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        title={`Copy ${conversation.channel === 'email' ? 'email' : 'phone number'}`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {!shouldShowContactName(conversation) && (
                    <button
                      onClick={() => copyToClipboard(getCleanContactInfo(conversation))}
                      className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors ml-10 sm:ml-0"
                      title={`Copy ${conversation.channel === 'email' ? 'email' : 'phone number'}`}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Additional conversation info - show only AI tools if present */}
                {conversation.metadata?.has_tool_calls && (
                  <div className="flex items-center gap-4 text-sm text-gray-500 mt-3">
                    <div className="flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      <span>{conversation.metadata.tool_call_count} AI tools used</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className={`flex-1 overflow-y-auto relative ${conversation ? 'md:pb-0 pb-4' : ''}`}>
                {/* Task Section - sticky at top of scroll container */}
                <TaskSection 
                  conversationId={conversationId} 
                  onTaskUpdate={(updatedTasks) => {
                    // Notify parent component about task changes if callback exists
                    if (window.updateConversationTasks) {
                      window.updateConversationTasks(conversationId, updatedTasks);
                    }
                  }}
                />

                {/* Messages content - Add mobile top spacing */}
                <div className={`p-6 space-y-4 ${conversation ? 'md:pt-6' : ''}`} 
                     style={conversation ? { paddingTop: 'var(--mobile-header-height, 7rem)' } : {}}
                >
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-50 flex items-center justify-center">
                      <ChatCircle className="w-6 h-6 text-gray-300" />
                    </div>
                    <h3 className="text-gray-400 text-sm font-light mb-2">No messages found</h3>
                    <p className="text-gray-400 text-xs">
                      {conversation.latest_message || conversation.initial_message 
                        ? 'Message content available in conversation metadata' 
                        : 'Start the conversation by sending a message below'}
                    </p>
                    {/* Show latest message if available in metadata */}
                    {(conversation.latest_message || conversation.initial_message) && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg max-w-md mx-auto">
                        <p className="text-sm text-gray-600">
                          {conversation.latest_message || conversation.initial_message}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatMessageTime(conversation.last_activity || conversation.created_at)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  messages
                    .filter(message => {
                      // Filter out assistant messages that contain "Tool Call:"
                      if (message.sender_type === 'assistant' && 
                          (message.message_content || message.content || '').includes('Tool Call:')) {
                        return false;
                      }
                      return true;
                    })
                    .map((message, index) => (
                    <div key={message.id || index} className={`flex ${message.sender_type === 'customer' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${getMessageStyle(message.sender_type)}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {getSenderIcon(message.sender_type)}
                          <span className="text-xs opacity-75 font-medium">
                            {getSenderLabel(message.sender_type)}
                          </span>
                          <span className="text-xs opacity-50 ml-auto">
                            {formatMessageTime(message.created_at)}
                          </span>
                        </div>

                        <p className="text-sm leading-relaxed">{message.message_content || message.content}</p>

                        {/* Tool call indicator */}
                        {message.metadata?.tool_calls && message.metadata.tool_calls.length > 0 && (
                          <div className="mt-3 p-2 bg-blue-50 rounded text-xs border-l-2 border-blue-200">
                            <div className="flex items-center gap-1 mb-1">
                              <Lightning className="w-3 h-3 text-blue-600" />
                              <span className="text-blue-800 font-medium">AI Tool Executed</span>
                            </div>
                            {message.metadata.tool_calls.map((tool, i) => (
                              <div key={i} className="text-blue-700 font-mono">
                                {tool.function?.name || tool.name || 'Unknown Tool'}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
                </div>
              </div>

              {/* Message Composer */}
              <MessageComposer
                conversationId={conversationId}
                channel={conversation.channel || 'sms'}
                onMessageSent={handleMessageSent}
                disabled={conversation.status === 'completed'}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChatConversationDetailsPage; 